// Word-level highlight inside a changed side-by-side row. The row already tints
// the whole block, so this narrows the eye down to the words that actually moved,
// the way a GitHub line diff does.

interface Token {
	value: string
	start: number
	end: number
}

const WORD = /\S+/g

function tokenize(text: string): Token[] {
	const tokens: Token[] = []
	for (const match of text.matchAll(WORD)) {
		const start = match.index
		tokens.push({
			value: match[0].toLowerCase(),
			start,
			end: start + match[0].length,
		})
	}
	return tokens
}

// Indexes of `own` tokens with no partner in `other`, via a plain LCS.
function changedTokenIndexes(own: Token[], other: Token[]): Set<number> {
	const n = own.length
	const m = other.length
	const table: number[][] = Array.from({ length: n + 1 }, () =>
		new Array<number>(m + 1).fill(0),
	)
	for (let i = n - 1; i >= 0; i--) {
		for (let j = m - 1; j >= 0; j--) {
			const row = table[i]
			const nextRow = table[i + 1]
			if (!row || !nextRow) continue
			row[j] =
				own[i]?.value === other[j]?.value
					? (nextRow[j + 1] ?? 0) + 1
					: Math.max(nextRow[j] ?? 0, row[j + 1] ?? 0)
		}
	}

	const changed = new Set<number>()
	let i = 0
	let j = 0
	while (i < n) {
		if (j < m && own[i]?.value === other[j]?.value) {
			i++
			j++
			continue
		}
		const down = table[i + 1]?.[j] ?? 0
		const across = table[i]?.[j + 1] ?? 0
		if (j >= m || down >= across) {
			changed.add(i)
			i++
		} else {
			j++
		}
	}
	return changed
}

function toRanges(
	tokens: Token[],
	changed: Set<number>,
): { start: number; end: number }[] {
	const ranges: { start: number; end: number }[] = []
	for (let i = 0; i < tokens.length; i++) {
		const token = tokens[i]
		if (!token || !changed.has(i)) continue
		const last = ranges[ranges.length - 1]
		if (last && token.start - last.end <= 1) last.end = token.end
		else ranges.push({ start: token.start, end: token.end })
	}
	return ranges
}

// A block whose every word changed is already fully tinted by the row, and
// peppering it with word spans just adds noise.
const FULL_CHANGE_RATIO = 0.9

export type WordDiffSide = "insert" | "delete"

export interface WordDiffRange {
	start: number
	end: number
}

// Character ranges of `ownText` with no partner in `otherText`. Empty when
// nothing changed or the whole block did.
export function changedWordRanges(
	ownText: string,
	otherText: string,
): WordDiffRange[] {
	const ownTokens = tokenize(ownText)
	if (ownTokens.length === 0) return []

	const changed = changedTokenIndexes(ownTokens, tokenize(otherText))
	if (changed.size === 0) return []
	if (changed.size / ownTokens.length >= FULL_CHANGE_RATIO) return []

	return toRanges(ownTokens, changed)
}

export interface TextSegment {
	text: string
	start: number
}

// One string for all text runs, with a space at each block start so the last
// word of one block never fuses with the first of the next.
export function layoutSegments(
	parts: readonly { text: string; blockStart: boolean }[],
): { fullText: string; segments: TextSegment[] } {
	let fullText = ""
	const segments: TextSegment[] = []
	for (const part of parts) {
		if (part.blockStart && fullText.length > 0) fullText += " "
		segments.push({ text: part.text, start: fullText.length })
		fullText += part.text
	}
	return { fullText, segments }
}

// The slice of `ranges` that falls inside `segment`, in segment-local offsets.
export function rangesWithin(
	ranges: readonly WordDiffRange[],
	segment: TextSegment,
): WordDiffRange[] {
	const end = segment.start + segment.text.length
	return ranges
		.filter((range) => range.end > segment.start && range.start < end)
		.map((range) => ({
			start: Math.max(range.start, segment.start) - segment.start,
			end: Math.min(range.end, end) - segment.start,
		}))
}

const BLOCK_TAGS = new Set([
	"P",
	"H1",
	"H2",
	"H3",
	"H4",
	"H5",
	"H6",
	"LI",
	"TD",
	"TH",
	"BLOCKQUOTE",
	"PRE",
	"DIV",
	"FIGCAPTION",
])

function blockAncestor(node: Node, container: Node): Node {
	let current: Node | null = node.parentNode
	while (current && current !== container) {
		if (BLOCK_TAGS.has(current.nodeName)) return current
		current = current.parentNode
	}
	return container
}

// Returns `html` with changed words wrapped in `<span data-diff-word>`. Falls
// back to the input untouched when there is no DOM, nothing to compare, or the
// whole block changed.
export function markChangedWords(
	html: string,
	otherText: string,
	side: WordDiffSide,
): string {
	if (typeof document === "undefined") return html

	const container = document.createElement("div")
	container.innerHTML = html

	const textNodes: Text[] = []
	const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
	let current = walker.nextNode()
	while (current) {
		textNodes.push(current as Text)
		current = walker.nextNode()
	}
	if (textNodes.length === 0) return html

	let previousBlock: Node | null = null
	const { fullText, segments } = layoutSegments(
		textNodes.map((node) => {
			const block = blockAncestor(node, container)
			const blockStart = block !== previousBlock
			previousBlock = block
			return { text: node.data, blockStart }
		}),
	)
	const ranges = changedWordRanges(fullText, otherText)
	if (ranges.length === 0) return html

	// Wrapping splits nodes, so walk the original list with its precomputed
	// offsets rather than re-reading the tree.
	textNodes.forEach((node, index) => {
		const segment = segments[index]
		if (!segment) return
		const local = rangesWithin(ranges, segment)
		if (local.length === 0) return

		const parent = node.parentNode
		if (!parent) return

		const fragment = document.createDocumentFragment()
		let cursor = 0
		for (const { start, end } of local) {
			if (start > cursor) {
				fragment.append(node.data.slice(cursor, start))
			}
			const span = document.createElement("span")
			span.setAttribute("data-diff-word", side)
			span.textContent = node.data.slice(start, end)
			fragment.append(span)
			cursor = end
		}
		if (cursor < node.data.length) {
			fragment.append(node.data.slice(cursor))
		}
		parent.replaceChild(fragment, node)
	})
	return container.innerHTML
}
